import React, { useState } from "react";
import PitchSvg from "./PitchSvg";
import { getShotMapCalibration } from "../utils/shotMapCalibration";
import "./ShotMapWithFilters.css";

const { INNER_LEFT_PX, INNER_TOP_PX, INNER_RIGHT_PX, INNER_BOTTOM_PX } =
  getShotMapCalibration();

const INNER_WIDTH_PX = INNER_RIGHT_PX - INNER_LEFT_PX;
const INNER_HEIGHT_PX = INNER_BOTTOM_PX - INNER_TOP_PX;

const SQUARE_SIZE_PX = 8.2;
const DIAMOND_SIZE_PX = 11.2;
const TRIANGLE_SIZE_PX = 10.2;
const MARKER_RADIUS_PX = 4.5;
const MARKER_STROKE_PX = 0.9;
const LABEL_FONT_SIZE_PX = 7.2;
const SMALL_LABEL_FONT_SIZE_PX = 6.2;
const LABEL_OFFSET_X = 0;
const LABEL_OFFSET_Y = 0;

const renderMarkerSvgElement = (shape, stroke, x, y, handlers) => {
  const common = {
    fill: "none",
    stroke,
    strokeWidth: MARKER_STROKE_PX,
    style: { pointerEvents: "all" },
    className: "shot-marker",
    ...handlers,
  };
  if (shape === "circle") {
    return <circle cx={x} cy={y} r={MARKER_RADIUS_PX} {...common} />;
  }
  if (shape === "square") {
    const half = SQUARE_SIZE_PX / 2;
    return (
      <rect
        x={x - half}
        y={y - half}
        width={SQUARE_SIZE_PX}
        height={SQUARE_SIZE_PX}
        {...common}
      />
    );
  }
  if (shape === "diamond") {
    const half = DIAMOND_SIZE_PX / 2;
    const points = [
      `${x},${y - half}`,
      `${x + half},${y}`,
      `${x},${y + half}`,
      `${x - half},${y}`,
    ].join(" ");
    return <polygon points={points} {...common} />;
  }
  const half = TRIANGLE_SIZE_PX / 2;
  const points = [
    `${x},${y - half}`,
    `${x + half},${y + half}`,
    `${x - half},${y + half}`,
  ].join(" ");
  return <polygon points={points} {...common} />;
};

export default function ShotMapCanvas({ markers = [], summaryText }) {
  const [hovered, setHovered] = useState(null);

  const clearHover = () => setHovered(null);
  return (
    <>
      {summaryText ? <div className="map-summary">{summaryText}</div> : null}
      <div className="pitch-container">
        <PitchSvg className="pitch-bg" />
        <svg
          className="pitch-overlay"
          viewBox={`${INNER_LEFT_PX} ${INNER_TOP_PX} ${INNER_WIDTH_PX} ${INNER_HEIGHT_PX}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {markers.map((marker) => (
            <g key={marker.id}>
              {renderMarkerSvgElement(
                marker.shape,
                marker.stroke,
                marker.x,
                marker.y,
                {
                  onMouseEnter: () => {
                    const xPct = (marker.x - INNER_LEFT_PX) / INNER_WIDTH_PX;
                    const yPct = (marker.y - INNER_TOP_PX) / INNER_HEIGHT_PX;
                    setHovered({
                      text: marker.tooltipText,
                      xPct,
                      yPct,
                    });
                  },
                  onMouseLeave: clearHover,
                },
              )}
              {marker.labelText ? (
                <text
                  x={marker.x + LABEL_OFFSET_X}
                  y={
                    marker.y +
                    LABEL_OFFSET_Y +
                    (marker.shape === "triangle" ? 2.0 : 0) +
                    (marker.shape === "square" ? 0.8 : 0)
                  }
                  fontSize={
                    String(marker.labelText).replace(/\D/g, "").length >= 2
                      ? SMALL_LABEL_FONT_SIZE_PX
                      : LABEL_FONT_SIZE_PX
                  }
                  fontWeight="bold"
                  fontFamily="DejaVu Sans"
                  fill="#000000"
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {marker.labelText}
                </text>
              ) : null}
            </g>
          ))}
        </svg>
        {hovered ? (
          <div
            className="shot-tooltip"
            style={{
              left: `${hovered.xPct * 100}%`,
              top: `${hovered.yPct * 100}%`,
            }}
          >
            {hovered.text}
          </div>
        ) : null}
      </div>
    </>
  );
}
